import React from 'react';
import { Printer, Zap, DollarSign, Gauge, ArrowDownUp, Circle, LayoutGrid } from 'lucide-react';

/**
 * Компонент секции параметров принтера для 3D-печати
 */
const PrinterParametersSection = ({ 
  formData, 
  handleInputChange, 
  errors,
  printerTypes
}) => {
  // Определяем, нужно ли показывать поле для диаметра сопла
  const showNozzleDiameter = formData.printerType === 'fdm';
  
  return (
    <div className="mb-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Printer className="w-5 h-5 mr-2 text-teal-500" />
        Параметры принтера и процесса
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Тип принтера */}
        <div>
          <label htmlFor="printerType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Тип принтера
          </label>
          <select
            id="printerType"
            name="printerType"
            value={formData.printerType}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.printerType ? 'input-error' : ''}`}
          >
            {printerTypes.map((printer) => (
              <option key={printer.id} value={printer.id}>
                {printer.name}
              </option>
            ))}
          </select>
          {errors.printerType && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.printerType}
            </p>
          )}
        </div>

        {/* Стоимость принтера */}
        <div>
          <label htmlFor="printerCost" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <DollarSign className="w-4 h-4 mr-1 text-teal-500" />
            Стоимость принтера (₽)
          </label>
          <input
            type="text"
            id="printerCost"
            name="printerCost"
            value={formData.printerCost}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.printerCost ? 'input-error' : ''}`}
            placeholder="Введите стоимость"
          />
          {errors.printerCost && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.printerCost}
            </p>
          )}
        </div>

        {/* Стоимость электроэнергии */}
        <div>
          <label htmlFor="electricityCost" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Zap className="w-4 h-4 mr-1 text-teal-500" />
            Стоимость электроэнергии (₽/кВт⋅ч)
          </label>
          <input
            type="text"
            id="electricityCost"
            name="electricityCost"
            value={formData.electricityCost}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.electricityCost ? 'input-error' : ''}`}
            placeholder="Введите стоимость"
          />
          {errors.electricityCost && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.electricityCost}
            </p>
          )}
        </div>

        {/* Потребление электроэнергии */}
        <div>
          <label htmlFor="printerConsumption" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Потребление энергии (кВт)
          </label>
          <input
            type="text"
            id="printerConsumption"
            name="printerConsumption"
            value={formData.printerConsumption}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.printerConsumption ? 'input-error' : ''}`}
            placeholder="Введите потребление"
          />
          {errors.printerConsumption && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.printerConsumption}
            </p>
          )}
        </div>

        {/* Скорость печати */}
        <div>
          <label htmlFor="printSpeed" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Gauge className="w-4 h-4 mr-1 text-teal-500" />
            Скорость печати (мм/с)
          </label>
          <input
            type="text"
            id="printSpeed"
            name="printSpeed"
            value={formData.printSpeed}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.printSpeed ? 'input-error' : ''}`}
            placeholder="Введите скорость"
          />
          {errors.printSpeed && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.printSpeed}
            </p>
          )}
        </div>

        {/* Высота слоя */}
        <div>
          <label htmlFor="layerHeight" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <ArrowDownUp className="w-4 h-4 mr-1 text-teal-500" />
            Высота слоя (мм)
          </label>
          <input
            type="text"
            id="layerHeight"
            name="layerHeight"
            value={formData.layerHeight}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.layerHeight ? 'input-error' : ''}`}
            placeholder="Введите высоту слоя"
          />
          {errors.layerHeight && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.layerHeight}
            </p>
          )}
        </div>

        {/* Диаметр сопла (только для FDM) */}
        {showNozzleDiameter && (
          <div>
            <label htmlFor="nozzleDiameter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Circle className="w-4 h-4 mr-1 text-teal-500" />
              Диаметр сопла (мм)
            </label>
            <input
              type="text"
              id="nozzleDiameter"
              name="nozzleDiameter"
              value={formData.nozzleDiameter}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.nozzleDiameter ? 'input-error' : ''}`}
              placeholder="Введите диаметр сопла"
            />
            {errors.nozzleDiameter && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.nozzleDiameter}
              </p>
            )}
          </div>
        )}

        {/* Плотность заполнения */}
        <div>
          <label htmlFor="infillDensity" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <LayoutGrid className="w-4 h-4 mr-1 text-teal-500" />
            Плотность заполнения (%)
          </label>
          <input
            type="text"
            id="infillDensity"
            name="infillDensity"
            value={formData.infillDensity}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.infillDensity ? 'input-error' : ''}`}
            placeholder="Введите плотность"
          />
          {errors.infillDensity && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.infillDensity}
            </p>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-100 dark:border-blue-800">
          <div className="flex items-start">
            <Printer className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-2 mt-1 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
                Влияние параметров печати
              </h4>
              <p className="text-xs text-blue-600 dark:text-blue-400">
                Более высокая скорость сокращает время печати, но может снизить качество. Меньшая высота слоя повышает детализацию, но увеличивает время печати.
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-100 dark:border-blue-800">
          <div className="flex items-start">
            <LayoutGrid className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-2 mt-1 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
                Выбор плотности заполнения
              </h4>
              <p className="text-xs text-blue-600 dark:text-blue-400">
                Для декоративных моделей достаточно 10-15%, для функциональных деталей рекомендуется 20-30%, для прочных изделий - 40-60%.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrinterParametersSection;